import { Injectable, inject, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { debounceTime, throttleTime } from 'rxjs/operators';

/**
 * Shared service for handling scroll and resize events efficiently
 * Prevents multiple event listeners from being created across components
 * Optimized to run outside Angular zone for better performance
 */
@Injectable({
	providedIn: 'root',
})
export class ScrollVisibilityService {
	private ngZone = inject(NgZone);
	private scrollSubject = new Subject<Event>();
	private resizeSubject = new Subject<Event>();
	private listenersInitialized = false;
	private rafId: number | null = null;

	public scroll$: Observable<Event>;
	public resize$: Observable<Event>;

	constructor() {
		// Create optimized observables with debounce and throttle
		// Use longer debounce for resize, shorter throttle for scroll
		this.scroll$ = this.scrollSubject.asObservable().pipe(
			throttleTime(150), // Throttle scroll events to reduce frequency
		);
		this.resize$ = this.resizeSubject.asObservable().pipe(
			debounceTime(250), // Debounce resize events (fire after user stops resizing)
		);

		this.initializeListeners();
	}

	/**
	 * Initialize shared event listeners (only once)
	 */
	private initializeListeners(): void {
		if (this.listenersInitialized) return;

		this.ngZone.runOutsideAngular(() => {
			// Use capture phase for better performance
			window.addEventListener('scroll', this.onScroll.bind(this), { passive: true, capture: true });
			window.addEventListener('resize', this.onResize.bind(this), { passive: true, capture: true });
			document.addEventListener('scroll', this.onScroll.bind(this), { passive: true, capture: true });
		});

		this.listenersInitialized = true;
	}

	private onScroll(event: Event): void {
		// Cancel pending RAF if exists
		if (this.rafId !== null) {
			cancelAnimationFrame(this.rafId);
		}

		// Use requestAnimationFrame to batch scroll events
		this.rafId = requestAnimationFrame(() => {
			this.rafId = null;
			// Emit outside Angular zone - subscribers will handle change detection if needed
			this.scrollSubject.next(event);
		});
	}

	private onResize(event: Event): void {
		// Emit resize events outside Angular zone
		this.resizeSubject.next(event);
	}
}
